import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  ColorLens as ColorLensIcon,
  FontDownload as FontIcon,
  RoundedCorner as RadiusIcon,
  CropSquare as StyleIcon,
  GridView as GridIcon,
  Visibility as VisibilityIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import { DisplaySettings as DisplaySettingsType } from '../../../types/storeSettings';
import { useStoreSettings } from '../../../contexts/StoreSettingsContext';

interface VisualThemeEditorProps {
  settings: DisplaySettingsType;
  onSave: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: boolean) => void;
}

const fontOptions = [
  { value: 'Cairo', label: 'Cairo (افتراضي)' },
  { value: 'Tajawal', label: 'Tajawal' },
  { value: 'Almarai', label: 'Almarai' },
  { value: 'El Messiri', label: 'El Messiri' },
  { value: 'Roboto', label: 'Roboto (English)' },
  { value: 'Inter', label: 'Inter (English)' }
];

const buttonStyleOptions = [
  { value: 'solid', label: 'ممتلئ (Solid)' },
  { value: 'outline', label: 'مفرغ (Outline)' },
  { value: 'flat', label: 'مسطح (Flat)' }
];

const productCardStyleOptions = [
  { value: 'classic', label: 'كلاسيكي (Classic)' },
  { value: 'modern-shadow', label: 'ظل عصري (Modern Shadow)' },
  { value: 'minimal', label: 'بسيط (Minimal)' },
  { value: 'bordered', label: 'بإطار (Bordered)' }
];

const productCardSizeOptions = [
  { value: 'small', label: 'صغير' },
  { value: 'medium', label: 'متوسط' },
  { value: 'large', label: 'كبير' }
];

const defaultSections = [
  { id: 'banners', label: 'البانرات الترويجية', enabled: true },
  { id: 'promotions', label: 'العروض الخاصة', enabled: true },
  { id: 'featured', label: 'المنتجات المميزة', enabled: true },
  { id: 'bestsellers', label: 'الأكثر مبيعاً', enabled: true },
  { id: 'latest', label: 'أحدث المنتجات', enabled: true }
];

const VisualThemeEditor: React.FC<VisualThemeEditorProps> = ({
  settings,
  onSave,
  onError,
  onSuccess
}) => {
  const { updateSection } = useStoreSettings();
  const [formData, setFormData] = useState<DisplaySettingsType>(settings);
  const [editing, setEditing] = useState(false);

  // تحديث البيانات المحلية عند تغيير الإعدادات الخارجية
  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  const handleChange = (name: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSectionToggle = (id: string, enabled: boolean) => {
    const currentSections = formData.homepageSections || defaultSections;
    const updatedSections = currentSections.map(section =>
      section.id === id ? { ...section, enabled } : section
    );
    handleChange('homepageSections', updatedSections);
  };

  const handleMoveSection = (index: number, direction: 'up' | 'down') => {
    const currentSections = [...(formData.homepageSections || defaultSections)];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= currentSections.length) return;

    // تبديل الأماكن
    const temp = currentSections[index];
    currentSections[index] = currentSections[targetIndex];
    currentSections[targetIndex] = temp;

    handleChange('homepageSections', currentSections);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      onSave(true);
      onError(null);

      const dataToSave = {
        ...formData,
        // التأكد من وجود ترتيب الأقسام
        homepageSections: formData.homepageSections || defaultSections
      };

      await updateSection('display', dataToSave);
      onSuccess(true);
      setEditing(false);

      // تنشيط تغيير الثيم في الصفحة فوراً
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('themeChanged', {
          detail: { themeId: formData.theme || 'default' }
        }));
      }
    } catch (error) {
      console.error('Error saving visual theme settings:', error);
      onError('حدث خطأ أثناء حفظ إعدادات المظهر المرئي');
    } finally {
      onSave(false);
    }
  };

  const currentSections = formData.homepageSections || defaultSections;

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2" fontWeight="bold">
          التخصيص المرئي (Visual Editor)
        </Typography>

        {!editing ? (
          <Button
            startIcon={<EditIcon />}
            onClick={() => setEditing(true)}
            variant="outlined"
          >
            تعديل الإعدادات المرئية
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setFormData(settings);
                setEditing(false);
              }}
            >
              إلغاء
            </Button>
            <Button
              type="submit"
              startIcon={<SaveIcon />}
              variant="contained"
              color="primary"
            >
              حفظ التغييرات
            </Button>
          </Box>
        )}
      </Box>

      {/* زر لتفعيل التحرير مباشرة للمساعدة */}
      {!editing && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={() => setEditing(true)}
          >
            انقر هنا لتعديل وحفظ الألوان والمظهر المرئي
          </Button>
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {/* قسم الألوان */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }} elevation={1}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ColorLensIcon color="primary" /> الألوان المخصصة للموقع
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>اللون الأساسي (Primary)</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="color"
                    value={formData.primaryColor || '#0d51d6'}
                    disabled={!editing}
                    onChange={(e) => handleChange('primaryColor', e.target.value)}
                    style={{ width: '40px', height: '40px', border: '1px solid #ccc', borderRadius: '4px', cursor: editing ? 'pointer' : 'default' }}
                  />
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{formData.primaryColor || '#0d51d6'}</Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>اللون الثانوي (Secondary)</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="color"
                    value={formData.secondaryColor || '#f50057'}
                    disabled={!editing}
                    onChange={(e) => handleChange('secondaryColor', e.target.value)}
                    style={{ width: '40px', height: '40px', border: '1px solid #ccc', borderRadius: '4px', cursor: editing ? 'pointer' : 'default' }}
                  />
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{formData.secondaryColor || '#f50057'}</Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>لون الخلفية الرئيسي</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="color"
                    value={formData.backgroundColor || '#ffffff'}
                    disabled={!editing}
                    onChange={(e) => handleChange('backgroundColor', e.target.value)}
                    style={{ width: '40px', height: '40px', border: '1px solid #ccc', borderRadius: '4px', cursor: editing ? 'pointer' : 'default' }}
                  />
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{formData.backgroundColor || '#ffffff'}</Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>لون النصوص</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="color"
                    value={formData.textColor || '#333333'}
                    disabled={!editing}
                    onChange={(e) => handleChange('textColor', e.target.value)}
                    style={{ width: '40px', height: '40px', border: '1px solid #ccc', borderRadius: '4px', cursor: editing ? 'pointer' : 'default' }}
                  />
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{formData.textColor || '#333333'}</Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>خلفية الأزرار الافتراضية</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="color"
                    value={formData.buttonBg || '#0d51d6'}
                    disabled={!editing}
                    onChange={(e) => handleChange('buttonBg', e.target.value)}
                    style={{ width: '40px', height: '40px', border: '1px solid #ccc', borderRadius: '4px', cursor: editing ? 'pointer' : 'default' }}
                  />
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{formData.buttonBg || '#0d51d6'}</Typography>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>نصوص الأزرار الافتراضية</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="color"
                    value={formData.buttonText || '#ffffff'}
                    disabled={!editing}
                    onChange={(e) => handleChange('buttonText', e.target.value)}
                    style={{ width: '40px', height: '40px', border: '1px solid #ccc', borderRadius: '4px', cursor: editing ? 'pointer' : 'default' }}
                  />
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{formData.buttonText || '#ffffff'}</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* قسم الخطوط والزر */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }} elevation={1}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FontIcon color="primary" /> التنسيقات والخطوط والأزرار
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="font-family-select-label">نوع الخط (Font Family)</InputLabel>
                  <Select
                    labelId="font-family-select-label"
                    value={formData.fontFamily || 'Cairo'}
                    disabled={!editing}
                    onChange={(e) => handleChange('fontFamily', e.target.value)}
                    label="نوع الخط (Font Family)"
                  >
                    {fontOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="button-style-select-label">شكل الأزرار (Button Style)</InputLabel>
                  <Select
                    labelId="button-style-select-label"
                    value={formData.buttonStyle || 'solid'}
                    disabled={!editing}
                    onChange={(e) => handleChange('buttonStyle', e.target.value)}
                    label="شكل الأزرار (Button Style)"
                  >
                    {buttonStyleOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="button-radius-select-label">حواف الأزرار (Border Radius)</InputLabel>
                  <Select
                    labelId="button-radius-select-label"
                    value={formData.buttonRadius || '8px'}
                    disabled={!editing}
                    onChange={(e) => handleChange('buttonRadius', e.target.value)}
                    label="حواف الأزرار (Border Radius)"
                  >
                    <MenuItem value="0px">حادة (0px)</MenuItem>
                    <MenuItem value="4px">دائرية خفيفة (4px)</MenuItem>
                    <MenuItem value="8px">دائرية متوسطة (8px)</MenuItem>
                    <MenuItem value="12px">دائرية واضحة (12px)</MenuItem>
                    <MenuItem value="16px">منحنية (16px)</MenuItem>
                    <MenuItem value="24px">بيضوية (24px)</MenuItem>
                    <MenuItem value="50px">مستديرة كاملة (50px)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* قسم كروت المنتجات */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }} elevation={1}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <StyleIcon color="primary" /> تصميم بطاقة المنتج (Product Card)
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="card-style-select-label">نمط البطاقة (Card Style)</InputLabel>
                  <Select
                    labelId="card-style-select-label"
                    value={formData.productCardStyle || 'classic'}
                    disabled={!editing}
                    onChange={(e) => handleChange('productCardStyle', e.target.value)}
                    label="نمط البطاقة (Card Style)"
                  >
                    {productCardStyleOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="card-size-select-label">حجم البطاقة (Card Size)</InputLabel>
                  <Select
                    labelId="card-size-select-label"
                    value={formData.productCardSize || 'medium'}
                    disabled={!editing}
                    onChange={(e) => handleChange('productCardSize', e.target.value)}
                    label="حجم البطاقة (Card Size)"
                  >
                    {productCardSizeOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="desktop-cols-select-label">أعمدة العرض للحاسوب</InputLabel>
                  <Select
                    labelId="desktop-cols-select-label"
                    value={formData.productCardColumns || 4}
                    disabled={!editing}
                    onChange={(e) => handleChange('productCardColumns', parseInt(e.target.value as string))}
                    label="أعمدة العرض للحاسوب"
                  >
                    <MenuItem value={2}>عمودين (2)</MenuItem>
                    <MenuItem value={3}>3 أعمدة (3)</MenuItem>
                    <MenuItem value={4}>4 أعمدة (4)</MenuItem>
                    <MenuItem value={6}>6 أعمدة (6)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="mobile-cols-select-label">أعمدة العرض للهاتف</InputLabel>
                  <Select
                    labelId="mobile-cols-select-label"
                    value={formData.mobileProductColumns || 2}
                    disabled={!editing}
                    onChange={(e) => handleChange('mobileProductColumns', parseInt(e.target.value as string))}
                    label="أعمدة العرض للهاتف"
                  >
                    <MenuItem value={1}>عمود واحد (1)</MenuItem>
                    <MenuItem value={2}>عمودين (2)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.showQuickView !== false}
                      onChange={(e) => handleChange('showQuickView', e.target.checked)}
                      disabled={!editing}
                    />
                  }
                  label="تفعيل المعاينة السريعة للمنتج (Quick View)"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* ترتيب الصفحة الرئيسية */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }} elevation={1}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <GridIcon color="primary" /> ترتيب وعرض أقسام الصفحة الرئيسية
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              يمكنك تعطيل/تفعيل الأقسام وتغيير ترتيب ظهورها على الواجهة الرئيسية للمتجر عن طريق أزرار الترتيب (الأعلى والأسفل).
            </Typography>

            <List sx={{ width: '100%', bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
              {currentSections.map((section, index) => (
                <ListItem
                  key={section.id}
                  sx={{
                    borderBottom: index < currentSections.length - 1 ? '1px solid #eaeaea' : 'none',
                    bgcolor: section.enabled ? 'transparent' : '#f9f9f9',
                    opacity: section.enabled ? 1 : 0.6
                  }}
                >
                  <ListItemIcon>
                    <DragIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={section.label}
                    secondary={section.id === 'banners' ? 'البانرات الدوارة العليا' : section.id === 'promotions' ? 'عروض الصور الإعلانية' : 'قوائم المنتجات'}
                  />
                  <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {/* التبديل */}
                    <Switch
                      edge="end"
                      checked={section.enabled}
                      disabled={!editing}
                      onChange={(e) => handleSectionToggle(section.id, e.target.checked)}
                    />
                    
                    {/* الترتيب للأعلى */}
                    <IconButton
                      size="small"
                      disabled={!editing || index === 0}
                      onClick={() => handleMoveSection(index, 'up')}
                    >
                      <UpIcon fontSize="small" />
                    </IconButton>

                    {/* الترتيب للأسفل */}
                    <IconButton
                      size="small"
                      disabled={!editing || index === currentSections.length - 1}
                      onClick={() => handleMoveSection(index, 'down')}
                    >
                      <DownIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {editing && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setFormData(settings);
              setEditing(false);
            }}
          >
            إلغاء التغييرات
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            حفظ التغييرات المرئية
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default VisualThemeEditor;
